import axios from 'axios';
import { store } from '../store';
import { logout, setCredentials } from '../store/slices/authSlice';

const httpClient = axios.create({
  baseURL: import.meta.env.VITE_API_BASE_URL, // e.g. http://localhost:5000
  withCredentials: true,
  headers: {
    'Content-Type': 'application/json',
    Accept: 'application/json'
  }
});

// Request interceptor: attach accessToken if present
httpClient.interceptors.request.use(
  (config) => {
    const state = store.getState();
    const token = state?.auth?.accessToken;

    // If request body is FormData, remove JSON Content-Type so axios/browser can set multipart boundary
    try {
      if (config && config.data && typeof FormData !== 'undefined' && config.data instanceof FormData) {
        if (config.headers && config.headers['Content-Type']) delete config.headers['Content-Type'];
      }
    } catch (e) {
      // ignore
    }

    if (token) {
      // eslint-disable-next-line no-param-reassign
      config.headers.Authorization = `Bearer ${token}`;
    }

    return config;
  },
  (error) => Promise.reject(error)
);

// Helper to call refresh-token endpoint
async function refreshTokenRequest(refreshToken) {
  const response = await axios.post(
    `${import.meta.env.VITE_API_BASE_URL}/api/v1/refresh-token`,
    { refreshToken },
    { withCredentials: true }
  );
  return response.data;
}

let isRefreshing = false;
let failedQueue = [];

function processQueue(error, token = null) {
  failedQueue.forEach((prom) => {
    if (error) {
      prom.reject(error);
    } else {
      prom.resolve(token);
    }
  });
  failedQueue = [];
}

// Response interceptor: handle 401 and refresh token
httpClient.interceptors.response.use(
  (response) => response,
  async (error) => {
    const originalRequest = error.config;
    const status = error?.response?.status;

    if (status !== 401 || originalRequest._retry) {
      return Promise.reject(error);
    }

    const state = store.getState();
    const currentRefreshToken = state?.auth?.refreshToken;

    if (!currentRefreshToken) {
      store.dispatch(logout());
      return Promise.reject(error);
    }

    if (isRefreshing) {
      return new Promise((resolve, reject) => {
        failedQueue.push({ resolve, reject });
      })
        .then((newToken) => {
          originalRequest.headers.Authorization = `Bearer ${newToken}`;
          return httpClient(originalRequest);
        })
        .catch((err) => Promise.reject(err));
    }

    originalRequest._retry = true;
    isRefreshing = true;

    try {
      const data = await refreshTokenRequest(currentRefreshToken);
      const { accessToken, refreshToken } = data || {};

      if (accessToken) {
        store.dispatch(setCredentials({ accessToken, refreshToken }));
        processQueue(null, accessToken);
        originalRequest.headers.Authorization = `Bearer ${accessToken}`;
        return httpClient(originalRequest);
      }

      store.dispatch(logout());
      processQueue(new Error('Unable to refresh token'));
      return Promise.reject(error);
    } catch (err) {
      store.dispatch(logout());
      processQueue(err);
      return Promise.reject(err);
    } finally {
      isRefreshing = false;
    }
  }
);

export default httpClient;
